import { invalidate } from '@react-three/fiber';
import { useEffect, useMemo } from 'react';
import {
    BufferGeometry,
    Color,
    DoubleSide,
    Float32BufferAttribute,
    InstancedBufferAttribute,
    InstancedMesh,
    LineBasicMaterial,
    LineDashedMaterial,
    LineSegments,
    Matrix4,
    Mesh,
    MeshBasicMaterial,
    PlaneGeometry,
    ShaderMaterial,
    ShapeUtils,
    Vector2,
    Vector3
} from 'three';

import { buildAtlas, FONT_SIZE } from 'app/modules/wallforming/components/scenes/objects/dimensions/buildAtlas';
import { DIMENSION_COLOR } from 'shared/constants';

import type { DimensionDrawing } from 'app/modules/wallforming/components/scenes/objects/dimensions/core/dimensionLayout';
import type { DisplayMapping, Vec2, ViewFrame } from 'app/modules/wallforming/components/scenes/objects/dimensions/core/viewFrame';

interface DimensionDrawingRendererProps {
    drawing: DimensionDrawing;
    frame: ViewFrame;
    mapping: DisplayMapping;
}

// atlas entries are rendered without wrapping
const NO_WRAP_WIDTH = 1000;
const atlasKey = (text: string) => `${text}\0${Math.round(NO_WRAP_WIDTH * FONT_SIZE)}`;

const LINE_DEPTH = 0.002;
const TEXT_DEPTH = 0.004;
const BASE_RENDER_ORDER = 300;
const OVERLAY_RENDER_ORDER = 1000;

const TEXT_VERT = /* glsl */ `
    attribute vec4 uvRect;
    attribute vec2 textScale;
    attribute vec3 textColor;
    varying vec2 vUv;
    varying vec3 vColor;

    void main() {
        vec4 localPos = vec4(position.x * textScale.x, position.y * textScale.y, 0.0, 1.0);
        gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * localPos;
        vUv = vec2(uv.x, 1.0 - uv.y) * uvRect.zw + uvRect.xy;
        vColor = textColor;
    }
`;

const TEXT_FRAG = /* glsl */ `
    uniform sampler2D atlas;
    varying vec2 vUv;
    varying vec3 vColor;

    void main() {
        vec4 s = texture2D(atlas, vUv);
        if (s.a < 0.05) discard;
        gl_FragColor = vec4(vColor, s.r);
    }
`;

const textPlane = new PlaneGeometry(1, 1);
const tmpColor = new Color();

function to3D(frame: ViewFrame, p: Vec2, depth: number, target = new Vector3()): Vector3 {
    return target
        .copy(frame.u)
        .multiplyScalar(p.x)
        .addScaledVector(frame.v, p.y)
        .addScaledVector(frame.n, depth);
}

function buildFillGeometry(drawing: DimensionDrawing, frame: ViewFrame, depth: number): BufferGeometry | null {
    const positions: number[] = [];
    const colors: number[] = [];
    const indices: number[] = [];
    const point = new Vector3();

    for (const fill of drawing.fills) {
        const [contour, ...holes] = fill.polygon;
        if (!contour || contour.length < 3) {
            continue;
        }

        const toVector2 = (ring: Vec2[]) => ring.map((p) => new Vector2(p.x, p.y));
        const triangles = ShapeUtils.triangulateShape(toVector2(contour), holes.map(toVector2));
        const base = positions.length / 3;
        tmpColor.set(fill.color);

        for (const p of [contour, ...holes].flat()) {
            to3D(frame, p, depth, point);
            positions.push(point.x, point.y, point.z);
            colors.push(tmpColor.r, tmpColor.g, tmpColor.b, fill.opacity);
        }
        for (const [a, b, c] of triangles) {
            indices.push(base + a, base + b, base + c);
        }
    }

    if (indices.length === 0) {
        return null;
    }

    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
    geometry.setAttribute('color', new Float32BufferAttribute(colors, 4));
    geometry.setIndex(indices);
    return geometry;
}

function buildLineGeometry(
    lines: DimensionDrawing['lines'],
    frame: ViewFrame,
    depth: number,
    withColors: boolean
): BufferGeometry | null {
    if (lines.length === 0) {
        return null;
    }

    const positions: number[] = [];
    const colors: number[] = [];
    const point = new Vector3();
    for (const line of lines) {
        for (const p of [line.a, line.b]) {
            to3D(frame, p, depth, point);
            positions.push(point.x, point.y, point.z);
        }
        if (withColors) {
            tmpColor.set(line.color);
            colors.push(tmpColor.r, tmpColor.g, tmpColor.b, tmpColor.r, tmpColor.g, tmpColor.b);
        }
    }

    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
    if (withColors) {
        geometry.setAttribute('color', new Float32BufferAttribute(colors, 3));
    }
    return geometry;
}

function buildTextMesh(drawing: DimensionDrawing, frame: ViewFrame, depth: number, material: ShaderMaterial) {
    if (drawing.texts.length === 0) {
        return null;
    }

    const { texture, uvRects, atlasWidth, atlasHeight } = buildAtlas(
        drawing.texts.map((t) => ({ text: t.text, fontSize: 1, maxWidth: NO_WRAP_WIDTH }))
    );
    material.uniforms.atlas.value = texture;

    const count = drawing.texts.length;
    const geometry = textPlane.clone();
    const uvArray = new Float32Array(count * 4);
    const scaleArray = new Float32Array(count * 2);
    const colorArray = new Float32Array(count * 3);
    const mesh = new InstancedMesh(geometry, material, count);

    const matrix = new Matrix4();
    const xAxis = new Vector3();
    const yAxis = new Vector3();
    const position = new Vector3();

    drawing.texts.forEach((text, i) => {
        const uv = uvRects.get(atlasKey(text.text));
        if (!uv) {
            return;
        }

        // text is always upright in the (u, v) basis; 90° turns it counter-clockwise
        if (text.rotation === 90) {
            xAxis.copy(frame.v);
            yAxis.copy(frame.u).negate();
        } else {
            xAxis.copy(frame.u);
            yAxis.copy(frame.v);
        }
        to3D(frame, text.position, depth, position);
        matrix.makeBasis(xAxis, yAxis, frame.n).setPosition(position);
        mesh.setMatrixAt(i, matrix);

        scaleArray[i * 2] = (uv.width * atlasWidth * text.size) / FONT_SIZE;
        scaleArray[i * 2 + 1] = (uv.height * atlasHeight * text.size) / FONT_SIZE;
        uvArray.set([uv.x, uv.y, uv.width, uv.height], i * 4);
        tmpColor.set(text.color);
        colorArray.set([tmpColor.r, tmpColor.g, tmpColor.b], i * 3);
    });

    geometry.setAttribute('uvRect', new InstancedBufferAttribute(uvArray, 4));
    geometry.setAttribute('textScale', new InstancedBufferAttribute(scaleArray, 2));
    geometry.setAttribute('textColor', new InstancedBufferAttribute(colorArray, 3));
    mesh.instanceMatrix.needsUpdate = true;
    mesh.frustumCulled = false;
    return { mesh, texture };
}

/** Renders a 2D dimension drawing in the view plane of the frame (coordinates relative to the frame origin). */
export const DimensionDrawingRenderer = ({ drawing, frame, mapping }: DimensionDrawingRendererProps) => {
    const isOverlay = mapping.mode === 'overlay';
    const renderOrder = isOverlay ? OVERLAY_RENDER_ORDER : BASE_RENDER_ORDER;

    const materials = useMemo(() => {
        // overlay is an annotation layer on top of the model; the projection respects scene depth
        const common = { depthTest: !isOverlay, depthWrite: false, transparent: true };
        return {
            fill: new MeshBasicMaterial({ ...common, side: DoubleSide, vertexColors: true }),
            line: new LineBasicMaterial({ ...common, vertexColors: true }),
            dashed: new LineDashedMaterial({ ...common, color: DIMENSION_COLOR, dashSize: 0.15, gapSize: 0.06 }),
            text: new ShaderMaterial({
                ...common,
                side: DoubleSide,
                uniforms: { atlas: { value: null } },
                vertexShader: TEXT_VERT,
                fragmentShader: TEXT_FRAG
            })
        };
    }, [isOverlay]);

    useEffect(
        () => () => {
            Object.values(materials).forEach((m) => m.dispose());
        },
        [materials]
    );

    const objects = useMemo(() => {
        const fillGeometry = buildFillGeometry(drawing, frame, mapping.depth);
        const lineGeometry = buildLineGeometry(drawing.lines, frame, mapping.depth + LINE_DEPTH, true);
        const dashedGeometry = buildLineGeometry(drawing.dashedLines, frame, mapping.depth + LINE_DEPTH, false);
        const text = buildTextMesh(drawing, frame, mapping.depth + TEXT_DEPTH, materials.text);

        const fill = fillGeometry ? new Mesh(fillGeometry, materials.fill) : null;
        const lines = lineGeometry ? new LineSegments(lineGeometry, materials.line) : null;
        const dashed = dashedGeometry ? new LineSegments(dashedGeometry, materials.dashed) : null;
        dashed?.computeLineDistances();

        const ordered = [fill, dashed, lines, text?.mesh];
        ordered.forEach((object, index) => {
            if (object) {
                object.renderOrder = renderOrder + index;
                object.frustumCulled = false;
            }
        });

        return { fill, lines, dashed, text };
    }, [drawing, frame, mapping.depth, materials, renderOrder]);

    useEffect(() => {
        invalidate();
        return () => {
            objects.fill?.geometry.dispose();
            objects.lines?.geometry.dispose();
            objects.dashed?.geometry.dispose();
            objects.text?.mesh.geometry.dispose();
            objects.text?.mesh.dispose();
            objects.text?.texture.dispose();
            // the canvas renders on demand: repaint so removed dimensions disappear immediately
            invalidate();
        };
    }, [objects]);

    return (
        <>
            {objects.fill && <primitive object={objects.fill} />}
            {objects.dashed && <primitive object={objects.dashed} />}
            {objects.lines && <primitive object={objects.lines} />}
            {objects.text && <primitive object={objects.text.mesh} />}
        </>
    );
};
